import { skipToken } from "@reduxjs/toolkit/query";
import type EntityMember from "_shared/entity-members/entity-member";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { useCallback, useMemo } from "react";
import { useGetProposalReviewerCandidatesInfiniteQuery } from "work/proposals/reviewers/_api/proposal-reviewer-candidate-api";
import ProposalReviewerCandidate from "work/proposals/reviewers/proposal-reviewer-candidate";

type UseInfiniteProposalReviewerCandidatesArgs = {
  negotiationId?: string;
  search: string;
  /** Whether the leader is the vendor's; only their team members are offered from another firm. */
  asVendor?: boolean;
  /** The vendor team as the page has it, unsaved edits included. Ignored unless `asVendor`. */
  vendorTeamMembers?: EntityMember[];
  skip?: boolean;
};

/**
 * The reviewer popover's directory: every candidate page loaded so far, in the order the server
 * returned them, and a way to load the next.
 *
 * The same shape `useInfiniteUserCoworkers` returned, so the popover changed its source and not how
 * it pages.
 */
export default function useInfiniteProposalReviewerCandidates(args: UseInfiniteProposalReviewerCandidatesArgs) {
  // A stable key for the team, so an unchanged team on a re-render asks nothing new.
  const teamKey = args.asVendor
    ? [...new Set((args.vendorTeamMembers ?? []).map((member) => `${member.userId.value}:${member.legalEntityId.value}`))]
        .sort()
        .join(",")
    : "";
  const teamMembers = useMemo(() => (teamKey ? teamKey.split(",") : undefined), [teamKey]);
  const query = useGetProposalReviewerCandidatesInfiniteQuery(
    args.skip
      ? skipToken
      : { negotiationId: args.negotiationId, search: args.search, asVendor: args.asVendor, teamMembers },
    // The candidates exclude the current reviewers, and those change while the popover is closed.
    { refetchOnMountOrArgChange: true },
  );
  const { data, error, fetchNextPage, hasNextPage, isFetching, isLoading, refetch } = query;

  const candidates = useMemo(() => {
    const pages = data?.pages ?? [];
    const lastPage = pages.at(-1);
    if (!lastPage) return PaginatedResponse.empty<ProposalReviewerCandidate>();

    return PaginatedResponse.from(
      ProposalReviewerCandidate.fromJSONArray(pages.flatMap((page) => page.data)),
      { ...lastPage.headers, hasNext: hasNextPage },
    );
  }, [data?.pages, hasNextPage]);

  const loadMore = useCallback(() => {
    if (args.skip || isFetching || !hasNextPage) return;
    void fetchNextPage();
  }, [args.skip, fetchNextPage, hasNextPage, isFetching]);

  const retry = useCallback(() => {
    if (args.skip || isFetching) return;
    void refetch();
  }, [args.skip, isFetching, refetch]);

  return {
    candidates,
    isLoading: isFetching,
    isInitialLoading: isLoading || (isFetching && candidates.isEmpty),
    error,
    loadMore,
    retry,
  };
}
